import { Fragment, useMemo, useState } from 'react';
import {
  SlateLabelEnum,
  type ContestSettings,
  type PayoutSchedule,
  ContestSegmentType,
} from '@betterpool/api-types/contests-service';
import useTranslation from 'next-translate/useTranslation';
import { useMantineTheme } from '@mantine/core';

import dt from '~/testing';
import { SegmentedControl } from '~/domains/common/components/SegmentedControl/SegmentedControl';
import { Text } from '~/domains/design-system/Text';
import { CurrencyConvertor } from '~/components/utils/formatters';

import { SectionGrid, SectionGridCol, SectionNote } from '../components';
import { useAdditionalPayoutSettings } from '~/domains/contest/domains/common/hooks/useAdditionalPayoutSettings';
import type { Contest } from '~/domains/contest/domains/services/interfaces/contest.interface';

type PrizesProps = {
  prizes: PayoutSchedule[];
  slateLabel?: SlateLabelEnum;
  segments?: ContestSettings['segments'];
  contest: Contest;
};

const enum SegmentTab {
  OVERALL = 'overall',
  SEGMENT = 'segment',
}

function PrizeValue({
  prize,
  isFirstPrize,
  showUpdatedPayout,
  originalPrizePool,
  currentPrizePool,
  theme,
}: {
  prize: PayoutSchedule;
  isFirstPrize: boolean;
  showUpdatedPayout: boolean;
  originalPrizePool: number | null;
  currentPrizePool: number | null;
  theme: any;
}) {
  const shouldShowUpdatedPayout =
    showUpdatedPayout && isFirstPrize && originalPrizePool && currentPrizePool;

  if (!shouldShowUpdatedPayout) {
    return <Text variant="subhead-medium">{prize.value}</Text>;
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: /* eslint-disable-line local-rules/use-theme-tokens */ '5px' }}>
      <Text
        variant="subhead-medium"
        style={{
          textDecoration: 'line-through',
          color: theme.colors.gray[6],
        }}
      >
        {CurrencyConvertor(originalPrizePool / 100)}
      </Text>
      <Text variant="subhead-medium" style={{ color: theme.colors.gold[0] }}>
        {CurrencyConvertor(currentPrizePool / 100)}
      </Text>
    </div>
  );
}

function PrizesTable({ prizes = [], slateLabel, segments, contest }: PrizesProps) {
  const { t } = useTranslation('contest');
  const theme = useMantineTheme();
  const { showUpdatedPayout, originalPrizePool, currentPrizePool } =
    useAdditionalPayoutSettings(contest);
  const [activeTab, setActiveTab] = useState<SegmentTab>(SegmentTab.OVERALL);
  const sortedPrizes = useMemo(() => [...prizes].sort((a, b) => a.order - b.order), [prizes]);
  const sortedSegmentPrizes = useMemo(
    () => (segments ? [...segments.prizes].sort((a, b) => a.order - b.order) : []),
    [segments]
  );
  const isSegmentedPayout = segments?.segmentType === ContestSegmentType.MULTI_SLATE;
  const isSlatePayout = segments?.segmentType === ContestSegmentType.SINGLE_SLATE;

  const segmentLabel = useMemo(() => {
    if (isSegmentedPayout) return t('detail.prizes.tabs.segments');
    if (isSlatePayout) {
      if (slateLabel === SlateLabelEnum.WEEK) return t('detail.prizes.tabs.slatePayoutWeek');
      if (slateLabel === SlateLabelEnum.TOURNAMENT)
        return t('detail.prizes.tabs.slatePayoutTournament');
      if (slateLabel === SlateLabelEnum.SLATE) return t('detail.prizes.tabs.slatePayoutSlate');
      return t('detail.prizes.tabs.slatePayoutSlate');
    }
    return null;
  }, [t, isSegmentedPayout, isSlatePayout, slateLabel]);

  const tabs = [
    { label: t('detail.prizes.tabs.overall'), value: SegmentTab.OVERALL },
    { label: segmentLabel, value: SegmentTab.SEGMENT },
  ];

  const items = activeTab === SegmentTab.OVERALL ? sortedPrizes : sortedSegmentPrizes;

  return (
    <SectionNote mb={4}>
      {isSegmentedPayout || isSlatePayout ? (
        <SegmentedControl
          value={activeTab}
          onChange={(v) => {
            setActiveTab(v as SegmentTab);
          }}
          data={tabs.map(({ label, value }) => ({
            value,
            label,
          }))}
          w="100%"
          mb="lg"
        />
      ) : null}
      {items.map((prize, i) => (
        <Fragment key={prize.value}>
          <SectionGrid data-test-id={dt.contestDetail.section.prizes.row}>
            <SectionGridCol span={9} md={8}>
              <Text variant="body-medium">{prize.label}</Text>
            </SectionGridCol>
            <SectionGridCol span={3} md={4}>
              <PrizeValue
                prize={prize}
                isFirstPrize={i === 0}
                showUpdatedPayout={showUpdatedPayout}
                originalPrizePool={originalPrizePool}
                currentPrizePool={currentPrizePool}
                theme={theme}
              />
            </SectionGridCol>
          </SectionGrid>
        </Fragment>
      ))}
    </SectionNote>
  );
}

export default PrizesTable;
